/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useEffect, useState } from 'react';
import {
	Avatar,
	Box,
	Group,
	Paper,
	Skeleton,
	Text,
	Image,
	Alert,
	CloseButton,
} from '@mantine/core';
import { useElementSize } from '@mantine/hooks';

// Layouts
import { Header } from '@layouts/Header';

// Styles
import classes from './additionality-projects.module.css';

// Store
import { useTranslation } from 'react-i18next';

// Router
import { useNavigate, useParams } from 'react-router';

// Api
import { useAdditionalityProject } from '@features/catalogs/additionality-projects/details/api/get-public-additionality-project-by-id';

// Components
import { Button } from '@components/atoms';

// Icons
import {
	IconDownload,
	IconQuestionMark,
} from '@tabler/icons-react';
import { IconInfoCircle } from '@tabler/icons-react';
import LinkedinLogo from '@assets/icons/linkedin-button.svg';
import XLogo from '@assets/icons/x-button.svg';

// constants
import {
	LINKEDIN_URL,
	NOT_APPLICABLE,
	X_URL,
} from '@config/constants/common';
import { getCountryName } from '@utils/countries';
import { paths } from '@config/constants/paths';

// UI local components
import { OrganizationProject } from '@components/organisms/OrganizationProject';

// Types
import { TFileMetadata } from '@global-types/index';
import { Loading } from '@components/atoms/Loading/Loading';
import { DescriptionRenderer } from '@components/atoms/DescriptionRenderer';

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */
type SidebarLink = {
	id: string;
	label: string;
	path: string;
	icon1: string;
	iconActive1: string;
};

export type SidebarItem = {
	id: string;
	path: string;
	label: string;
	icon: string;
	iconActive: string;
	group: string;
	disabled?: boolean;
	links?: SidebarLink[];
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const MeasurabilityProjectsPublic = () => {
	/* ********************************** Hooks ********************************* */

	const { t } = useTranslation();

	const navigate = useNavigate();

	const { ref } = useElementSize();

	const { projectId } = useParams();

	const icon = <IconInfoCircle />;

	const { data, isFetching, isError } =
		useAdditionalityProject({
			projectId: projectId ? projectId : '',
		});

	const handleOpenLink = (
		url: string,
		socialMedia: string
	) => {
		switch (socialMedia) {
			case 'linkedin':
				window.open(`${LINKEDIN_URL}/${url}`, '_blank');
				break;
			case 'x':
				window.open(`${X_URL}/${url}`, '_blank');
				break;

			default:
				break;
		}
	};

	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		setTimeout(() => {
			setIsLoading(false);
		}, 1000);
	}, []);

	if (isLoading) return <Loading />;

	if (isError) {
		return (
			<Box className={classes.container} ref={ref}>
				<Paper className={classes.paperError}>
					<Alert
						variant="light"
						color="error"
						title={t('errors.invalidProjectIdTitle')}
						icon={icon}
					>
						{t('errors.invalidProjectId')}
					</Alert>
				</Paper>
			</Box>
		);
	}

	if (isFetching) {
		return (
			<Box className={classes.containerSkeletons}>
				<Skeleton
					height={'30%'}
					width={'100%'}
					radius="md"
				/>
				<Skeleton
					height={'3%'}
					width={'200px'}
					radius="md"
				/>
				<Skeleton
					height={'5%'}
					width={'100%'}
					radius="md"
				/>
				<Skeleton
					height={'3%'}
					width={'50%'}
					radius="md"
				/>
				<Skeleton
					height={'3%'}
					width={'50%'}
					radius="md"
				/>
				<Skeleton
					height={'3%'}
					width={'50%'}
					radius="md"
				/>
				<Skeleton
					height={'3%'}
					width={'50%'}
					radius="md"
				/>
				<Skeleton
					height={'3%'}
					width={'50%'}
					radius="md"
				/>
				<Skeleton
					height={'8%'}
					width={'100%'}
					radius="md"
				/>
				<Skeleton
					height={'5%'}
					width={'100%'}
					radius="md"
				/>
			</Box>
		);
	}

	const additionalityProject = data;

	if (!additionalityProject) return null;

	const {
		cover,
		name,
		dueDate,
		technology,
		targetEuro,
		description,
		organization,
		entity,
		location,
		social,
		coverage,
		publicSpecs,
		supportedBy,
	} = additionalityProject;

	const publicUser = {
		locale: { language: 'fr', timezone: 'CET' },
		firstName: 'N/A',
		lastName: '',
		email: 'N/A',
		role: 'N/A',
		createdAt: '2023-12-10',
		updatedAt: '2024-01-10',
		organization: 'N/A',
		id: '2',
	};

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.layout}>
			<Box className={classes.wrapperContent}>
				<Box className={classes.content}>
					<Header
						id={''}
						label={t('global.seekingFundingProjects')}
						user={null}
						onLogout={() => {}}
					/>
					{/*Main */}
					<Box className={classes.body}>
						<Box className={classes.container} ref={ref}>
							<Paper className={classes.paper}>
								<Group
									onClick={() => {
										navigate(
											paths.public
												.catalogAdditionalityProjects.path
										);
									}}
									justify="flex-end"
									mb={'md'}
								>
									<CloseButton />
								</Group>
								<Box className={classes.wrapper}>
									<Box className={classes.wrapperLeft}>
										{/*Bloc Organization */}
										<OrganizationProject
											contactProducer={publicUser}
											entity={entity}
											owner={publicUser}
											user={publicUser}
											organization={organization}
											cover={cover}
											isPublic
											supportedBy={supportedBy}
										/>
										{/*Bloc Project informations */}
										<Box className={classes.bloc2}>
											<Box className={classes.rowHeader}>
												<Text className={classes.title}>
													{name}
												</Text>
											</Box>
											<Box
												className={
													classes.bloc2Informations
												}
											>
												<Box
													className={
														classes.groupCountryInformations
													}
												>
													<Avatar
														className={
															classes.logoCountry
														}
														radius={'xl'}
														src={`https://flagcdn.com/w40/${location.countryCode.toLowerCase()}.png`}
														size={'xs'}
													>
														<IconQuestionMark />
													</Avatar>

													<Box
														className={
															classes.groupCountryTextInformations
														}
													>
														<Text
															className={
																classes.textCountryProject
															}
														>
															{getCountryName(
																location.countryCode
															)}
														</Text>
														<Text
															className={
																classes.textCountryPlaceProject
															}
														>
															{`/ ${location.place ? location.place : NOT_APPLICABLE}`}
														</Text>
													</Box>
												</Box>
												{/* Due date */}
												<Box className={classes.rowData}>
													<Box
														className={
															classes.containerLabel
														}
													>
														<Text
															className={
																classes.labelText
															}
														>
															{t('global.dueDate')}
														</Text>
													</Box>
													<Box
														className={
															classes.containerValue
														}
													>
														<Text
															className={
																classes.valueText
															}
														>
															{dueDate}
														</Text>
													</Box>
												</Box>

												{/* Technology */}
												<Box className={classes.rowData}>
													<Box
														className={
															classes.containerLabel
														}
													>
														<Text
															className={
																classes.labelText
															}
														>
															{t('global.projectType')}
														</Text>
													</Box>
													<Box
														className={
															classes.containerValue
														}
													>
														<Text
															className={
																classes.valueText
															}
														>
															{t(technology)}
														</Text>
													</Box>
												</Box>
												{/* Financal Needs */}
												<Box className={classes.rowData}>
													<Box
														className={
															classes.containerLabel
														}
													>
														<Text
															className={
																classes.labelText
															}
														>
															{t('global.financialNeeds')}
														</Text>
													</Box>
													<Box
														className={
															classes.containerValue
														}
													>
														<Text
															className={
																classes.valueText
															}
														>
															{targetEuro}
														</Text>
													</Box>
												</Box>

												<Box className={classes.rowData}>
													<Box
														className={
															classes.containerLabel
														}
													>
														<Text
															className={
																classes.labelText
															}
														>
															{t('global.coverage')}
														</Text>
													</Box>
													<Box
														className={
															classes.containerValue
														}
													>
														<Text
															className={
																classes.valueText
															}
														>
															{coverage}
														</Text>
													</Box>
												</Box>
												{/* Social media */}
												{social &&
													(social.linkedIn ||
														social.twitter) && (
														<Box
															className={classes.rowData}
														>
															<Box
																className={
																	classes.containerLabel
																}
															>
																<Text
																	className={
																		classes.labelText
																	}
																>
																	{t(
																		'global.socialMedia'
																	)}
																</Text>
															</Box>
															<Box
																className={
																	classes.containerValue
																}
															>
																<Group gap={'13.04px'}>
																	{social.linkedIn && (
																		<Image
																			className={
																				classes.socialMediaButton
																			}
																			fit="contain"
																			src={LinkedinLogo}
																			onClick={() =>
																				handleOpenLink(
																					social.linkedIn,
																					'linkedin'
																				)
																			}
																		/>
																	)}
																	{social.twitter && (
																		<Image
																			className={
																				classes.socialMediaButton
																			}
																			fit="contain"
																			src={XLogo}
																			onClick={() =>
																				handleOpenLink(
																					social.twitter,
																					'x'
																				)
																			}
																		/>
																	)}
																</Group>
															</Box>
														</Box>
													)}

												{/*Technical documents */}

												<Box
													className={
														classes.rowDataTechnicalDocument
													}
												>
													<Box
														className={
															classes.containerLabel
														}
													>
														<Text
															className={
																classes.labelText
															}
														>
															{t(
																'global.titleTechnicalDocuments'
															)}
														</Text>
													</Box>

													<Box
														className={
															classes.containerValue
														}
													>
														{publicSpecs &&
														Array.isArray(publicSpecs) &&
														publicSpecs.length > 0 ? (
															<Group>
																{publicSpecs.map(
																	(
																		fileMetaData: TFileMetadata
																	) => (
																		<Button
																			key={
																				fileMetaData.id
																			}
																			onClick={() => {
																				window.open(
																					fileMetaData.link,
																					'_blank'
																				);
																			}}
																			variant="outline"
																			leftSection={
																				<IconDownload
																					size={15}
																				/>
																			}
																			size={'xs'}
																		>
																			{
																				fileMetaData.filename
																			}
																		</Button>
																	)
																)}
															</Group>
														) : (
															<Text
																className={
																	classes.valueText
																}
															>
																{NOT_APPLICABLE}
															</Text>
														)}
													</Box>
												</Box>

												{/* Description */}
												<Box
													className={
														classes.rowDataDescription
													}
												>
													<Box
														className={
															classes.containerLabelDescription
														}
													>
														<Text
															className={
																classes.labelText
															}
														>
															{t('global.description')}
														</Text>
													</Box>
													<Box
														className={
															classes.containerValueDescription
														}
													>
														{description ? (
															<DescriptionRenderer
																description={description}
															/>
														) : (
															<Text
																className={
																	classes.valueText
																}
															>
																{NOT_APPLICABLE}
															</Text>
														)}
													</Box>
												</Box>
											</Box>
										</Box>
									</Box>
								</Box>
							</Paper>
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default MeasurabilityProjectsPublic;
